import React, { useState } from 'react';
import styled, { keyframes, css } from 'styled-components';

// 主题颜色
const theme = {
  primary: '#F47B20', // 橙色主题色
  secondary: '#FFF3E0', // 浅橙色背景
  text: '#333333',
  lightText: '#666666',
  border: '#FFD8B5',
  background: '#FFFFFF',
  checkIcon: '#FFFFFF',
};

// 容器样式
const Container = styled.div`
  font-family: 'PingFang SC', 'Helvetica Neue', Arial, sans-serif;
  max-width: 100%;
  margin: 0 auto;
  background: ${theme.background};
  color: ${theme.text};
`;

// 标题区域
const Header = styled.div`
  background-color: ${theme.primary};
  color: white;
  padding: 20px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: 28px;
  font-weight: bold;
`;

const ChecklistIcon = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  
  svg {
    width: 24px;
    height: 24px;
    color: ${theme.primary};
  }
`;

const BrandLogo = styled.div`
  font-size: 16px;
  font-weight: 500;
`;

// 表格样式
const Table = styled.div`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 15% 35% 10% 40%;
  background-color: ${theme.primary};
  color: white;
  font-size: 14px;
  font-weight: 500;
  
  @media (max-width: 768px) {
    grid-template-columns: 20% 40% 10% 30%;
  }
`;

const HeaderCell = styled.div`
  padding: 12px 8px;
  text-align: center;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  
  &:last-child {
    border-right: none;
  }
`;

// 维度行
const DimensionRow = styled.div`
  display: flex;
  background-color: ${theme.secondary};
  padding: 12px 16px;
  font-weight: bold;
  font-size: 16px;
  color: ${theme.primary};
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: ${theme.primary};
  }
`;

// 问题行
const QuestionRow = styled.div`
  display: grid;
  grid-template-columns: 15% 35% 10% 40%;
  border-bottom: 1px solid ${theme.border};
  
  @media (max-width: 768px) {
    grid-template-columns: 20% 40% 10% 30%;
  }
`;

// 维度单元格
const DimensionCell = styled.div`
  padding: 16px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-weight: 500;
  color: ${theme.primary};
  border-right: 1px solid ${theme.border};
  
  svg {
    width: 24px;
    height: 24px;
    margin-bottom: 8px;
  }
`;

// 问题单元格
const QuestionCell = styled.div`
  padding: 16px 8px;
  line-height: 1.5;
  border-right: 1px solid ${theme.border};
  font-weight: 500;
  color: ${theme.darkText};
  text-align: left;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;

`;

// 是否单元格
const YesNoCell = styled.div`
  padding: 16px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-right: 1px solid ${theme.border};
`;

// 新增：是/否选择框容器
const YesNoOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

// 新增：单个选择项
const Option = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

// 自定义复选框
const Checkbox = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 8px;
  border: 2px solid ${theme.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-right: 4px;
  background-color: ${props => props.checked ? theme.primary : 'transparent'};
  transition: all 0.2s ease;
  
  svg {
    color: white;
    width: 14px;
    height: 14px;
    opacity: ${props => props.checked ? 1 : 0};
    transition: opacity 0.2s ease;
  }
`;

const OptionLabel = styled.div`
  font-size: 14px;
  color: ${theme.text};
`;

// 解读单元格
const InterpretationCell = styled.div`
  padding: 16px 8px;
  line-height: 1.5;
  background-color: ${props => props.active ? '#FFF9F0' : 'transparent'};
  transition: background-color 0.3s ease;
  
  ${props => props.active && css`
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background-color: ${theme.primary};
    }
  `}
`;

// 警告图标
const WarningIcon = styled.div`
  color: ${theme.primary};
  margin-right: 8px;
  display: flex;
  align-items: center;
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

// 解读内容容器
const InterpretationContent = styled.div`
  display: flex;
  align-items: flex-start;
  font-size: 14px;
`;

// 移动端适配的折叠面板
const MobileAccordion = styled.div`
  display: none;
  
  @media (max-width: 576px) {
    display: block;
  }
`;

const MobileQuestionItem = styled.div`
  border-bottom: 1px solid ${theme.border};
  margin-bottom: 8px;
`;

const MobileQuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: ${props => props.active ? theme.secondary : 'white'};
  cursor: pointer;
`;

const MobileQuestionTitle = styled.div`
  flex: 1;
  padding-right: 12px;
`;

const MobileQuestionToggle = styled.div`
  display: flex;
  align-items: center;
`;

const MobileQuestionContent = styled.div`
  padding: 0 16px 16px;
  display: ${props => props.open ? 'block' : 'none'};
  background-color: ${theme.secondary};
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const MobileInterpretation = styled.div`
  margin-top: 12px;
  padding: 12px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  animation: ${fadeIn} 0.3s ease;
`;

// 移动端选项容器
const MobileOptions = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 12px;
`;

const SelfAssessmentTable = () => {
  // 状态管理 - 修改为记录"是"或"否"的状态
  const [responses, setResponses] = useState({});
  const [openAccordion, setOpenAccordion] = useState(null);

  // 切换响应状态
  const handleResponse = (id, value) => {
    setResponses(prev => ({
      ...prev,
      [id]: value
    }));
  };

  // 切换移动端折叠面板
  const toggleAccordion = (id) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };

  // 表格数据 - 更新为完整数据
  const tableData = [
    {
      dimension: "题意解析维度",
      icon: "target",
      questions: [
        {
          id: 1,
          question: "我是否根据题干要求，逐一明确各部分的任务？",
          interpretation: "如果回答否，你的问题是：未能准确理解题干任务，造成答非所问；可能导致某个答题方向遗漏造成重大失分。",
          suggestion: "建议加强对不同题型的题干分析训练，尝试在做题时圈画关键词，逐条列出任务要求，确保完整覆盖。"
        },
        {
          id: 2,
          question: "在作答之前，我是否能够快速判断出题型并制定清晰的作答思路？",
          interpretation: "如果回答否，你的问题是：题型识别不清，导致答题无章法；可能导致某个答题方向遗漏造成重大失分。",
          suggestion: "建议复习常见题型（如概括、分析、对策、公文、作文），通过专项训练迅速判断题型特点，并尝试在答题前列出简单提纲理清作答思路。"
        },
        {
          id: 3,
          question: "我是否能保证每次审题都能获取全部作答提示，不遗漏要作答的内容？",
          interpretation: "如果回答否，你的问题是：审题不够细致，遗漏了重要的作答提示；可能导致某个答题方向遗漏造成重大失分。",
          suggestion: "建议每次审题后用自己的话复述题目要求，确保每个任务都得到准确回应。"
        },
        {
          id: 4,
          question: "作文审题我能否从题干中分析出作文的主题、观点？",
          interpretation: "如果回答否，你的问题是：无法从题干信息中提炼主题观点，导致作文偏题；可能导致某个答题方向遗漏造成重大失分。",
          suggestion: "建议反复练习从题干中提炼核心主题，结合历年真题模拟作文审题，并在审题后简单写出主题和主要论点以验证自己的理解是否准确。"
        },
        {
          id: 5,
          question: "考场上面对没有做过的题目，我能否根据题干给的信息整理出相应的作答内容和思路？",
          interpretation: "如果回答否，你的问题是：题目不熟悉时无从下手，无法理清作答思路；可能导致某个答题方向遗漏造成重大失分。",
          suggestion: "建议练习面对新题目时快速分类提取信息，试着用结构化思维理清作答步骤，比如先抓关键词，再梳理核心逻辑，逐步形成答题思路。"
        }
      ]
    },
    {
      dimension: "材料阅读维度",
      icon: "eye",
      questions: [
        {
          id: 6,
          question: "阅读材料时，我能否从材料中找到题干要求我找的内容，并确保不遗漏？",
          interpretation: "如果回答否，你的问题是：抓取信息不全，导致漏掉重要要点；可能导致某个答题方向遗漏造成重大失分。",
          suggestion: "建议在阅读材料时使用标记法（如圈画关键词，勾画关键句等），逐段检查是否已涵盖题干要求的信息，并反复训练快速定位关键信息。"
        },
        {
          id: 7,
          question: "阅读材料时，我是否能标注出段落重点，而不是单纯的标记要点？",
          interpretation: "如果回答否，你的问题是：未能提炼段落主旨，导致前置词不准确或者遗漏关键信息；可能让阅卷人认为答案的准确性不足，从而影响得分。",
          suggestion: "建议每段阅读后试着写出一两句话的主旨总结，并用不同符号标注主旨句和核心信息，使材料内容层次更清晰。"
        },
        {
          id: 8,
          question: "阅读材料时，我是否能结合梳理清楚材料各个段落之间的逻辑关系？",
          interpretation: "如果回答否，你的问题是：未理清逻辑关系，导致答题时逻辑跳跃；可能导致某个答题方向遗漏造成重大失分。",
          suggestion: "建议练习用箭头或简单图表标注段落之间的因果、递进等逻辑关系，多做逻辑链条分析训练，让答案更加严谨清晰。"
        },
        {
          id: 9,
          question: "阅读材料时，如果材料是描述案例，我能否根据材料内容对案例做总结提炼？",
          interpretation: "如果回答否，你的问题是：案例总结不清，导致答案内容堆砌；可能导致某个答题方向遗漏造成重大失分。",
          suggestion: "建议在阅读案例材料时，先提炼出核心事件、人物、背景等关键信息，再通过列出要点的方式进行归纳总结，提升材料整合能力。"
        },
        {
          id: 10,
          question: "阅读材料时，如果材料是零散的有多个段落，我能否把这些段落的信息分类整合起来？",
          interpretation: "如果回答否，你的问题是：无法分类整合信息，导致答案要点分散；可能导致某个答题方向遗漏造成重大失分。",
          suggestion: "建议在阅读时先分段阅读，标记段落主旨句，再再根据分类规则做合并（比如同主旨合并、同主体合并等）。"
        }
      ]
    },
    {
      dimension: "信息加工维度",
      icon: "process",
      questions: [
        {
          id: 11,
          question: "在组织答案时，我是否能够根据材料给的信息做分析推导得出答案？",
          interpretation: "如果回答否，你的问题是：未能进行有效推导，答案局限于材料表面；可能导致某个答题方向遗漏造成重大失分。",
          suggestion: "建议在日常练习中加强对材料内容的分析，尝试结合背景知识或政策常识，将材料中的显性信息转化为深层次的分析结论，逐步提升思维的发散性和归纳能力。"
        },
        {
          id: 12,
          question: "在答提出建议、解决问题类题目时，我能否针对材料中已有的信息推导对策？",
          interpretation: "如果回答否，你的问题是：未能形成针对性对策，答案缺乏可行性；可能导致某个答题方向遗漏造成重大失分。",
          suggestion: "建议通过对常见问题类型（如环境保护、民生问题）的对策进行专题训练，梳理典型解决措施的逻辑路径，并在作答时结合材料信息迅速调整对策方向。"
        },
        {
          id: 13,
          question: "在写作文时，我能否把材料中的案例、数据、政策等素材转换成自己的论据？",
          interpretation: "如果回答否，你的问题是：未能将材料转化为独立论据，答案缺乏说服力；可能导致某个答题方向遗漏造成重大失分。",
          suggestion: "建议在日常写作练习中刻意练习转述技巧，尝试将材料中的案例重新概括成自己的观点支撑点，并用简洁有力的语言表述，提升答案的独立性和可信度。"
        },
        {
          id: 14,
          question: "在书写要点时，我能否根据材料的逻辑对要点进行分类、分条书写？",
          interpretation: "如果回答否，你的问题是：答题杂乱无章，分类不清；可能导致某个答题方向遗漏造成重大失分。",
          suggestion: `建议在作答时明确分条原则，如按照"问题-原因-对策"划分层次，用序号标明每个要点，确保答案有条理，层次分明。`
        },
        {
          id: 15,
          question: "在进行分析论证时，我能否结合材料内容进行多角度分析，而非单一视角？",
          interpretation: "如果回答否，你的问题是：答案单一视角，分析深度不够；可能导致某个答题方向遗漏造成重大失分。",
          suggestion: "建议练习从不同主体（如政府、企业、公众）的角度分析问题，通过设置不同的分析模板，逐步提升多角度思考的能力，让答案更加全面有说服力。"
        }
      ]
    },
    {
      dimension: "答案结构维度",
      icon: "structure",
      questions: [
        {
          id: 16,
          question: "我是否对大作文立意有清晰的规划，明确的总分论点？",
          interpretation: "如果回答否，你的问题是：未能明确主线观点，导致作文中心不突出；可能导致某个答题方向遗漏造成重大失分。",
          suggestion: "建议在作文前列出总分结构提纲，确定主论点和分论点，并用简洁的逻辑关系标明各论点之间的递进或并列关系，增强文章整体连贯性。"
        },
        {
          id: 17,
          question: "我的答案是否语言简洁，句式规范，卷面整洁？",
          interpretation: "如果回答否，你的问题是：语言表达冗长，格式混乱；可能导致某个答题方向遗漏造成重大失分。",
          suggestion: "建议多练习短句表达，控制句子长度，注重逻辑衔接，保持卷面整洁，避免出现潦草或不规范的格式。"
        }
      ]
    }
  ];

  // 桌面版渲染
  const renderDesktopTable = () => (
    <Table>
      <TableHeader>
        <HeaderCell>维度</HeaderCell>
        <HeaderCell>自测问题</HeaderCell>
        <HeaderCell>是/否</HeaderCell>
        <HeaderCell>对应解读</HeaderCell>
      </TableHeader>

      {tableData.map((section, index) => (
        <React.Fragment key={`section-${index}`}>
          <DimensionRow>
            {section.dimension}
          </DimensionRow>

          {section.questions.map((item) => (
            <QuestionRow key={item.id}>
              <DimensionCell>
                {section.icon === "target" && (
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" fill="currentColor" />
                  </svg>
                )}
                {section.icon === "eye" && (
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
                {section.icon === "process" && (
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 12H18L15 21L9 3L6 12H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
                {section.icon === "structure" && (
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 3H3V10H10V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M21 3H14V10H21V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M21 14H14V21H21V14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M10 14H3V21H10V14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
                {section.dimension}
              </DimensionCell>

              <QuestionCell>{item.question}</QuestionCell>

              <YesNoCell>
                <YesNoOptions>
                  <Option onClick={() => handleResponse(item.id, 'yes')}>
                    <Checkbox checked={responses[item.id] === 'yes'}>
                      {responses[item.id] === 'yes' && (
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </Checkbox>
                    <OptionLabel>是</OptionLabel>
                  </Option>

                  <Option onClick={() => handleResponse(item.id, 'no')}>
                    <Checkbox checked={responses[item.id] === 'no'}>
                      {responses[item.id] === 'no' && (
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </Checkbox>
                    <OptionLabel>否</OptionLabel>
                  </Option>
                </YesNoOptions>
              </YesNoCell>

              <InterpretationCell active={responses[item.id] === 'no' || responses[item.id] === undefined}>
                {responses[item.id] === undefined && (
                  <InterpretationContent>
                    <WarningIcon>
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 9V13M12 17H12.01M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </WarningIcon>
                    {item.interpretation}
                  </InterpretationContent>
                )}
                {responses[item.id] === 'no' && (
                  <InterpretationContent>
                    <WarningIcon>
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 9V13M12 17H12.01M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </WarningIcon>
                    {item.suggestion}
                  </InterpretationContent>
                )}
              </InterpretationCell>
            </QuestionRow>
          ))}
        </React.Fragment>
      ))}
    </Table>
  );

  // 移动端渲染
  const renderMobileTable = () => (
    <MobileAccordion>
      {tableData.map((section) => (
        <React.Fragment key={`mobile-section-${section.dimension}`}>
          <DimensionRow>{section.dimension}</DimensionRow>

          {section.questions.map((item) => (
            <MobileQuestionItem key={`mobile-question-${item.id}`}>
              <MobileQuestionHeader
                active={openAccordion === item.id}
                onClick={() => toggleAccordion(item.id)}
              >
                <MobileQuestionTitle>{item.question}</MobileQuestionTitle>
              </MobileQuestionHeader>

              <MobileQuestionContent open={openAccordion === item.id}>
                <MobileOptions>
                  <Option onClick={() => handleResponse(item.id, 'yes')}>
                    <Checkbox checked={responses[item.id] === 'yes'}>
                      {responses[item.id] === 'yes' && (
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </Checkbox>
                    <OptionLabel>是</OptionLabel>
                  </Option>

                  <Option onClick={() => handleResponse(item.id, 'no')}>
                    <Checkbox checked={responses[item.id] === 'no'}>
                      {responses[item.id] === 'no' && (
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </Checkbox>
                    <OptionLabel>否</OptionLabel>
                  </Option>
                </MobileOptions>

                {responses[item.id] === undefined && (
                  <MobileInterpretation>
                    <InterpretationContent>
                      <WarningIcon>
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 9V13M12 17H12.01M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </WarningIcon>
                      {item.interpretation}
                    </InterpretationContent>
                  </MobileInterpretation>
                )}

                {responses[item.id] === 'no' && (
                  <MobileInterpretation>
                    <InterpretationContent>
                      <WarningIcon>
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 9V13M12 17H12.01M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </WarningIcon>
                      {item.suggestion}
                    </InterpretationContent>
                  </MobileInterpretation>
                )}
              </MobileQuestionContent>
            </MobileQuestionItem>
          ))}
        </React.Fragment>
      ))}
    </MobileAccordion>
  );

  return (
    <Container>
      <Header>
        <HeaderTitle>
          <ChecklistIcon>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 11L12 14L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M21 12V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </ChecklistIcon>
          申论问题{<br />}自我诊断清单
        </HeaderTitle>
        <BrandLogo>
          步知1V1
          <img src='https://g.buzhi.com/img/logo.8861f94a.png' style={{ width: '69px', height: '18px', marginLeft: '8px', verticalAlign: 'middle' }} />
        </BrandLogo>
      </Header>

      {renderDesktopTable()}
    </Container>
  );
};

export default SelfAssessmentTable;